import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  ModalContent,
  Button,
  CellText,
  H1,
  ModalHeader,
  ModalFooter,
  ModalHeaderIconBox,
} from '@increasecard/typed-components';
import { MonetizationOn } from '@styled-icons/material/MonetizationOn';
import { Row } from '../../../components/common/Row';
import { ApiErrorMessage } from '../../../components/ErrorMessage';
import { useReversePayment } from '../../payments/paymentQueries';
import { useReverseInvoice } from '../invoiceQueries';
import { Payment, Invoice } from '../../../types';
import { formatCurrency } from '../../../utils/currencyUtils';

const CREDIT_NOTE_WIDTH = '500';

const StyledH1 = styled(H1)`
  color: var(--color-pay-dark50);
`;

const StyledCellText = styled(CellText)`
  color: var(--color-pay-dark50);
`;

export interface CreditNoteModalProps {
  visible: boolean;
  onClose: () => void;
  payment?: Payment | undefined | null;
  invoice?: Invoice | undefined | null;
}

export function CreditNoteModal({
  visible,
  onClose,
  payment,
  invoice,
}: CreditNoteModalProps): JSX.Element | null {
  const { t } = useTranslation();
  const paymentMutation = useReversePayment();
  const invoiceMutation = useReverseInvoice();

  const { isSuccess, isError, isLoading, reset, error } = payment
    ? paymentMutation
    : invoiceMutation;

  const handleConfirm = () => {
    if (payment) {
      paymentMutation.mutate(payment.id);
    } else if (invoice) {
      invoiceMutation.mutate(invoice.id);
    }
  };

  const entity = payment || invoice;

  const handleClose = () => {
    onClose();
    reset();
  };

  return (
    <Modal
      align="center"
      onClose={onClose}
      visible={visible}
      width={CREDIT_NOTE_WIDTH}
      onOk={() => null}
      onCancel={() => null}
    >
      <ModalHeaderIconBox
        colorPalette="pay"
        icon={<MonetizationOn size="48px" />}
      />
      <ModalContent align="center">
        <ModalHeader>{t('screens.credit_note.emit')}</ModalHeader>
        <StyledH1>
          {entity ? formatCurrency(entity.amount, entity.currency) : 0}
        </StyledH1>
        <StyledCellText>
          {t('screens.credit_note.invoice_amount')}
        </StyledCellText>
        <Row alignItems="flex-start">
          <CellText style={{ width: '100%' }}>
            {t('screens.credit_note.disclaimer')}
          </CellText>
        </Row>
        {isSuccess && t('screens.credit_note.success_message')}
        <ApiErrorMessage error={error} centered />
      </ModalContent>
      <ModalFooter align="center">
        {isError || isSuccess ? (
          <Button key="close" onClick={handleClose} buttonType="primary">
            {t('common.close')}
          </Button>
        ) : (
          <>
            <Button
              data-testid="cancel-button"
              onClick={handleClose}
              buttonType="invisible"
              disabled={isLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              data-testid="confirm-button"
              onClick={handleConfirm}
              isLoading={isLoading}
            >
              {t('common.refund_payment')}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
}
